import PageHeaderContainer from 'shared/components/PageHeader/container'
import React, { useState, useEffect, useCallback } from 'react'
import { AdminContainer } from '..'
import { Button } from 'semantic-ui-react'
import ContainerShell from 'shared/components/ContainerShell'
import ContainerShellIntro from 'shared/components/ContainerShellIntro'
import useModal from 'hooks/useModal'
import CreateOrUpdateTopicModal from './TopicModal'
import CreateOrUpdateTopicGroupModal from './TopicGroupModal'
import GroupContent from './GroupContent'
import TopicContent from './TopicContent'
import useSaveUserActivityLog, { ANSWER_TOPIC_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { toast } from 'libs/toast'
import useAxios from 'hooks/useAxios'
import ConfirmDeletionModal from './ConfirmDeletionModal'
import { LimitFeatureButton } from 'components/LimitFeatureButton'
import { CrownBadge } from 'components/ProFeatureLabel'

const topicInitialState = {
  id: null,
  status: null,
  description: '',
  name: '',
  original_name: '',
  groups: [],
  groupValues: [],
}
const topicGroupInitialState = {
  id: null,
  name: '',
  original_name: '',
}

const Topic = ({
  topicData,
  topicDataLoading,
  topicGroupData,
  topicGroupDataLoading,
  handleSwitchTab,
  tabName,
  handleToggleActive,
  setTopicState,
  setTopicGroupState,
  allowAdminOrAbove,
  updateTopicLoading,
  createTopicLoading,
  canCreateTopic,
  checkingCreatePermission,
}) => {
  const saveUserActivityLog = useSaveUserActivityLog()
  const [editingTopic, setEditingTopic] = useState(topicInitialState)
  const [editingTopicGroup, setEditingTopicGroup] = useState(topicGroupInitialState)
  const [deletingItem, setDeletingItem] = useState({ id: null, key: null, status: null })
  const [pagination, setPagination] = useState({
    topic: { current: 1, pageSize: 50 },
    topicGroup: { current: 1, pageSize: 50 },
  })
  const [filteredDataSource, setFilteredDataSource] = useState({
    topic: [],
    topicGroup: [],
  })
  const [searchText, setSearchText] = useState({
    topic: '',
    topicGroup: '',
  })

  const {
    Modal: TopicModal,
    open: openTopicModal,
    isOpen: isOpenTopicModal,
    close: closeTopicModal,
  } = useModal()

  const {
    Modal: TopicGroupModal,
    open: openTopicGroupModal,
    isOpen: isOpenTopicGroupModal,
    close: closeTopicGroupModal,
  } = useModal()

  const {
    Modal: DeletionModal,
    open: openDeletionModal,
    isOpen: isOpenDeletionModal,
    close: closeDeletionModal,
  } = useModal()

  const { fetch: deleteTopic, loading: isDeletingTopic } = useAxios({
    method: 'delete',
    skip: true,
  })

  const { fetch: deleteTopicGroup, loading: isDeletingTopicGroup } = useAxios({
    method: 'delete',
    url: `responses/topic-groups/${editingTopicGroup.id}/`,
    skip: true,
  })

  function handleEditAction(data) {
    if (tabName === 'topic') {
      const groupValues = []

      if (data.groups.length > 0) {
        data.groups.forEach((group) => {
          groupValues.push(group.name)
        })
      }

      setEditingTopic({ ...data, groupValues: groupValues, original_name: data.name })
      openTopicModal()
    } else {
      setEditingTopicGroup({ ...data, original_name: data.name })
      openTopicGroupModal()
    }
  }

  function resetInitialState() {
    setEditingTopic(topicInitialState)
    setEditingTopicGroup(topicGroupInitialState)
  }

  function handleCloseModal() {
    if (tabName === 'topic') {
      closeTopicModal()
    } else {
      closeTopicGroupModal()
    }
    resetInitialState()
  }

  function handlePageChange(page, pageSize, name) {
    setPagination((prev) => ({ ...prev, [name]: { current: page, pageSize: pageSize } }))
  }

  function handleSearch(e, name) {
    setSearchText((prev) => ({ ...prev, [name]: e.target.value }))

    const value = e.target.value.toLowerCase()
    updateFilteredData(value, name)
  }

  const updateFilteredData = useCallback(
    (value, name) => {
      let dataToFilter
      let filteredData

      if (name === 'topic') {
        dataToFilter = topicData
      } else {
        dataToFilter = topicGroupData
      }

      // Filter dataSource based on search text
      if (value.trim() === '') {
        filteredData = dataToFilter // Show all data when search value is empty
      } else {
        filteredData = dataToFilter.filter((item) => item.name.toLowerCase().includes(value))
      }

      // Update filteredDataSource and reset pagination to first page
      setFilteredDataSource((prev) => ({ ...prev, [name]: filteredData }))
      setPagination((prevPagination) => ({
        ...prevPagination,
        [name]: { ...prevPagination[name], current: 1 },
      }))
    },
    [topicData, topicGroupData]
  )

  function handleDelete(data) {
    setDeletingItem({ id: data.id, key: data.key, status: data.status })
    openDeletionModal()
  }

  function handleDeleteTopic() {
    deleteTopic({ url: `responses/topics/${deletingItem.id}/` })
      .then((res) => {
        if (res.status === 204) {
          setTopicState((prev) => {
            const filteredResults = prev.filter((item) => item.key !== deletingItem.key)

            return filteredResults
          })

          saveUserActivityLog({
            action: ANSWER_TOPIC_ACTION_NAME.delete_topic,
            statusCode: res.status,
            requestBody: {},
            target: `topic_${deletingItem.id}`,
          })

          toast.success('The topic was successfully deleted.')
          closeDeletionModal()
        }
      })
      .catch((err) => {
        saveUserActivityLog({
          action: ANSWER_TOPIC_ACTION_NAME.delete_topic,
          statusCode: err.response.status,
          errorResponseBody: err.response.data,
          requestBody: {},
          target: `topic_${deletingItem.id}`,
        })
        toast.error(
          'Uh oh. There was a problem deleting the topic. Please try again or contact Simplesat support.'
        )
        console.error(err)
      })
  }

  function handleDeleteTopicGroup() {
    deleteTopicGroup({ url: `responses/topic-groups/${deletingItem.id}/` })
      .then((res) => {
        if (res.status === 204) {
          toast.success('The topic group was successfully deleted.')
          setTopicGroupState((prev) => {
            const filteredResults = prev.filter((item) => item.key !== deletingItem.key)

            return filteredResults
          })

          saveUserActivityLog({
            action: ANSWER_TOPIC_ACTION_NAME.delete_topic_group,
            statusCode: res.status,
            requestBody: {},
            target: `topic_group_${deletingItem.id}`,
          })

          closeDeletionModal()
        }
      })
      .catch((err) => {
        saveUserActivityLog({
          action: ANSWER_TOPIC_ACTION_NAME.delete_topic_group,
          statusCode: err.response.status,
          errorResponseBody: err.response.data,
          requestBody: {},
          target: `topic_group_${deletingItem.id}`,
        })

        toast.error(
          'Uh oh. There was a problem deleting the new topic group. Please try again or contact Simplesat support.'
        )
        console.error(err)
      })
  }

  useEffect(() => {
    if (topicData) {
      if (searchText.topic) {
        updateFilteredData(searchText.topic.toLowerCase(), 'topic')
      } else {
        setFilteredDataSource((prev) => ({
          ...prev,
          topic: topicData,
        }))
      }
    }

    if (topicGroupData) {
      if (searchText.topicGroup) {
        updateFilteredData(searchText.topicGroup.toLowerCase(), 'topicGroup')
      } else {
        setFilteredDataSource((prev) => ({
          ...prev,
          topicGroup: topicGroupData,
        }))
      }
    }
  }, [topicData, topicGroupData, searchText.topic, searchText.topicGroup, updateFilteredData])

  return (
    <React.Fragment>
      <PageHeaderContainer
        title="Topics"
        rightSlot={
          allowAdminOrAbove ? (
            <React.Fragment>
              {tabName === 'topic' && (
                <LimitFeatureButton
                  featureLimit={null}
                  usageCount={null}
                  descriptionTitle={
                    <span className="flex items-center">
                      Custom topics unavailable <CrownBadge />
                    </span>
                  }
                  descriptionContent="Custom topics are available on a higher plan. Please upgrade to access this feature."
                  learnMoreLink="https://help.simplesat.io/en/articles/9526492-topics"
                  buttonText={'New topic'}
                  handleClick={openTopicModal}
                  loading={checkingCreatePermission}
                  isAllowedFeature={canCreateTopic}
                />
              )}
              {tabName === 'group' && (
                <CreateButton text="New topic group" openModal={openTopicGroupModal} />
              )}
            </React.Fragment>
          ) : null
        }
      />
      <AdminContainer>
        <ContainerShell
          intro={
            <ContainerShellIntro
              helpLinkLabel="Learn more about topics"
              helpLinkUrl="https://help.simplesat.io/en/articles/9526492-topics"
            />
          }
        >
          <div className="ui">
            {tabName === 'topic' && (
              <TopicContent
                topicDataLoading={topicDataLoading}
                handleSwitchTab={handleSwitchTab}
                tabName={tabName}
                handleToggleActive={handleToggleActive}
                handleSearch={handleSearch}
                pagination={pagination}
                searchText={searchText}
                filteredDataSource={filteredDataSource}
                handleEditAction={handleEditAction}
                handlePageChange={handlePageChange}
                allowAdminOrAbove={allowAdminOrAbove}
                handleDelete={handleDelete}
                updateTopicLoading={updateTopicLoading}
                createTopicLoading={createTopicLoading}
              />
            )}

            {tabName === 'group' && (
              <GroupContent
                topicGroupDataLoading={topicGroupDataLoading}
                handleSwitchTab={handleSwitchTab}
                tabName={tabName}
                handleSearch={handleSearch}
                pagination={pagination}
                searchText={searchText}
                filteredDataSource={filteredDataSource}
                handleEditAction={handleEditAction}
                handlePageChange={handlePageChange}
                allowAdminOrAbove={allowAdminOrAbove}
                handleDelete={handleDelete}
              />
            )}
          </div>
        </ContainerShell>
      </AdminContainer>
      {isOpenTopicModal && (
        <CreateOrUpdateTopicModal
          modal={TopicModal}
          isOpenTopicModal={isOpenTopicModal}
          topicData={topicData}
          close={handleCloseModal}
          editingTopic={editingTopic}
          setEditingTopic={setEditingTopic}
          topicGroups={topicGroupData}
          setTopicState={setTopicState}
        />
      )}
      {isOpenTopicGroupModal && (
        <CreateOrUpdateTopicGroupModal
          topicGroupData={topicGroupData}
          modal={TopicGroupModal}
          isOpenTopicGroupModal={isOpenTopicGroupModal}
          close={handleCloseModal}
          editingTopicGroup={editingTopicGroup}
          setEditingTopicGroup={setEditingTopicGroup}
          setTopicGroupState={setTopicGroupState}
        />
      )}
      {isOpenDeletionModal && (
        <ConfirmDeletionModal
          modal={DeletionModal}
          title={tabName === 'topic' ? 'Delete topic' : 'Delete topic group'}
          loading={tabName === 'topic' ? isDeletingTopic : isDeletingTopicGroup}
          description={`Are you sure you wish to delete this topic ${
            tabName !== 'topic' ? 'group' : ''
          }?`}
          close={closeDeletionModal}
          handleDelete={tabName === 'topic' ? handleDeleteTopic : handleDeleteTopicGroup}
        />
      )}
    </React.Fragment>
  )
}

const CreateButton = ({ text, openModal }) => {
  return (
    <React.Fragment>
      <Button id="create-survey-btn" primary onClick={openModal}>
        <i className="icon far fa-plus" /> {text}
      </Button>
    </React.Fragment>
  )
}

export default Topic



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Admin/Topics/Topic.js